import { ColumnDef } from "@tanstack/react-table";
import { useCallback } from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import { PayReferralButton } from "@/referrals/common/pay-referral-button";
import { ReferralOptionsDropdown } from "@/referrals/common/referral-options-dropdown";
import { REFERRAL_STATUS } from "@/referrals/constants";
import { IReferral } from "@/referrals/referral";

interface ActionsColContentProps {
  referral: IReferral;
  setPayoutReferral: (referral: IReferral) => void;
  onLost: (referralId: string) => void;
  onMoveToPaid: (referralId: string) => void;
  onDelete: (referralId: string) => void;
}

const ActionsColContent = ({
  referral,
  setPayoutReferral,
  onLost,
  onMoveToPaid,
  onDelete,
}: ActionsColContentProps) => {
  const handleLost = useCallback(() => {
    onLost(referral.id);
  }, [onLost, referral.id]);

  const handleMoveToPaid = useCallback(() => {
    onMoveToPaid(referral.id);
  }, [onMoveToPaid, referral.id]);

  const handleDelete = useCallback(() => {
    onDelete(referral.id);
  }, [onDelete, referral.id]);

  return (
    <Stack
      className="w-full"
      gap={2}
      direction="row"
      justify="end"
      align="center"
      onClick={(e) => e.stopPropagation()}
    >
      {/* As on the board: Pay is the closed referral's action. Other stages
          pay from the side panel. */}
      {referral.status === REFERRAL_STATUS.CLOSED && (
        <PayReferralButton onPay={() => setPayoutReferral(referral)} />
      )}
      <ReferralOptionsDropdown
        referral={referral}
        onLost={handleLost}
        onMoveToPaid={handleMoveToPaid}
        onDelete={handleDelete}
      />
    </Stack>
  );
};
interface ActionsColProps {
  setPayoutReferral: (referral: IReferral) => void;
  onLost: (referralId: string) => void;
  onMoveToPaid: (referralId: string) => void;
  onDelete: (referralId: string) => void;
}

const ActionsCol = ({
  setPayoutReferral,
  onLost,
  onMoveToPaid,
  onDelete,
}: ActionsColProps): ColumnDef<IReferral> => ({
  id: "actions",
  header: () => <span className="sr-only">Actions</span>,
  meta: {
    size: "130px",
  },
  cell: ({ row }) => (
    <ActionsColContent
      referral={row.original}
      setPayoutReferral={setPayoutReferral}
      onLost={onLost}
      onMoveToPaid={onMoveToPaid}
      onDelete={onDelete}
    />
  ),
  enableSorting: false,
  enableResizing: false,
  enablePinning: true,
});

export default ActionsCol;
